import {
	Component,
	ElementRef,
	Input,
	Output,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
	EventEmitter,
	ApplicationRef,
	forwardRef,
	ChangeDetectorRef,
	TrackByFunction,
	input,
	TemplateRef,
} from '@angular/core'
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms'
import { SearchableDropdownBindingMode } from '@lg/shared'
import { DataQueryFilter } from '@lg/shared'
import {
	EntityClassOnDb,
	EntityInstanceOnDb,
	FindManyOptions,
	ServerDataStoreClass,
} from '@lg/shared'
import {
	BehaviorSubject,
	switchMap,
	of,
	from,
	Subscription,
	Observable,
	Subject,
	debounceTime,
} from 'rxjs'

@Component({
	selector: 'lowgile-searchable-dropdown',
	templateUrl: './searchable-dropdown.component.html',
	styleUrls: ['./searchable-dropdown.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SearchableDropdownComponent),
			multi: true,
		},
	],
})
export class SearchableDropdownComponent<T extends EntityInstanceOnDb>
	implements OnInit, OnDestroy, ControlValueAccessor
{
	protected text = ''
	protected searchSubject = new BehaviorSubject<string | null>(null)
	value: T | null = null
	index: number = -1
	protected list: T[] = []

	data = input<T[] | Promise<T[]>>()
	@Input() label!: string
	@Input() bindingMode: SearchableDropdownBindingMode = 'object'
	// @Input() formBinding!: FormBinding
	@Input() formControl!: FormControl
	@Input() entity!: EntityClassOnDb
	@Input() serverDataStore!: ServerDataStoreClass
	@Input() minSearchLength = 3
	@Input() maxResults? = 20
	@Input() displayWith!: (input: T | null, index?: number) => string
	@Input() debouncingDelayMs = 250
	@Input() findOptions?: FindManyOptions<any>
	trackBy = input.required<TrackByFunction<T>>()
	@Input() allowClearing: boolean = true
	@Input() allClasses: Record<string, Record<string, any>> = {}
	itemTemplate = input<TemplateRef<any>>()

	@Output() optionSelected = new EventEmitter<T | null>()

	protected onChanged?: (value: any) => void
	private subscriptions: Subscription[] = []

	constructor(private cdRef: ChangeDetectorRef) {}

	ngOnInit(): void {
		this.updateInternalValue(this.formControl.value)

		this.subscriptions.push(
			this.formControl.valueChanges.subscribe((id) =>
				this.updateInternalValue(id),
			),

			this.searchSubject
				.pipe(
					debounceTime(this.debouncingDelayMs),
					switchMap((text) => this.executeSearch(text)),
				)
				.subscribe((list) => {
					this.list = list
					this.cdRef.markForCheck()
				}),
		)
	}

	ngOnDestroy() {
		this.subscriptions.forEach((s) => s.unsubscribe())
	}

	onKeyUp(event: KeyboardEvent) {
		const text = (event.target as HTMLInputElement).value
		if (text != this.text) {
			this.text = text
			this.searchSubject.next(text)
		}
	}

	onSelected(value: T | null) {
		this.value = value

		if (this.bindingMode == 'object') {
			this.formControl.setValue(value)
		} else {
			this.formControl.setValue(value?.id)
		}
		// (this.formBinding.object as any)[this.formBinding.property] = this.value?.id
		this.optionSelected.emit(this.value)
	}

	writeValue(obj: any): void {
		this.updateInternalValue(obj)
	}

	registerOnChange(fn: any): void {
		this.onChanged = fn
	}

	registerOnTouched(fn: any): void {
		// this.onTouched = fn
	}

	async updateInternalValue(objOrId: T | T['id']) {
		if (!objOrId) {
			this.value = null
		} else if (typeof objOrId == 'object') {
			this.value = objOrId
			this.index = this.list?.findIndex((item) => item.id === objOrId.id) ?? -1
		} else {
			const foundObject = this.findObjectWithId(objOrId)
			this.value = foundObject?.value ?? null
			this.index = foundObject?.index ?? -1
		}

		if (!this.value && typeof objOrId != 'object') {
			this.value = await this.serverDataStore.loadById<T>(this.entity, objOrId)
			this.list = [this.value!]
		}
		this.text = this.value
			? (this.displayWith(this.value, this.index) ?? '')
			: ''
		// this.onChanged?.(this.value)
		this.cdRef.markForCheck()
	}

	private executeSearch(text: string | null) {
		if (text == null || text.length < this.minSearchLength) return of([] as T[])

		const dataPromise = Promise.resolve(this.data())
		const resultPromise = dataPromise.then((data) => {
			if (data) {
				const filter = new DataQueryFilter(
					this.entity,
					{
						query: text,
					},
					this.allClasses,
				)
				return filter.filterList(data)
			}

			const loadPromise = this.serverDataStore.loadByQuery<T>(
				this.entity,
				{
					query: text,
				},
				{
					...this.findOptions,
					take: (this.maxResults || this.findOptions?.take) ?? 0,
				},
			)
			return loadPromise
		})

		return from(resultPromise)
	}

	private findObjectWithId(id: T['id']): { value: T; index: number } | null {
		if (!id) return null
		if (this.value?.id === id) return { value: this.value, index: this.index }

		const index = this.list?.findIndex((item) => item.id === id)
		if (index >= 0) return { value: this.list[index], index }

		return null
	}
}
